import {
  AHIcon,
  AHTag,
  AHText,
  AHTextFonts,
  Box,
  TouchableBox,
  useScaleFactor,
  useSpaceSize,
  useTheme,
} from "arkh";
import {
  PixelRatio,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from "react-native";
import React, { useCallback, useMemo } from "react";

import { DateTime } from "luxon";
import { TodoCircle } from "./TodoCircle";

import { graphql, useFragment, useMutation } from "react-relay";
import { TodoOverviewListItemWidget_Todo$key } from "./__generated__/TodoOverviewListItemWidget_Todo.graphql";
import { TodoOverviewListItemFooter_Todo$key } from "./__generated__/TodoOverviewListItemFooter_Todo.graphql";
import {
  TodoOverviewListItemSetStatusMutation,
  TodoStatus,
} from "./__generated__/TodoOverviewListItemSetStatusMutation.graphql";
import { TodoOverviewListItemExpireInfo_Todo$key } from "./__generated__/TodoOverviewListItemExpireInfo_Todo.graphql";
import { useEvent } from "../useEvent";

type TodoListItemProps = {
  isTopItem: boolean;
  isBottomItem: boolean;
  isFocused?: boolean;
  onPress?: (todoId: string, focused: boolean) => void;
  todo: TodoOverviewListItemWidget_Todo$key;
  style?: StyleProp<ViewStyle>;
  omitSingleTeamName?: boolean;
  interactive?: undefined | false;
};

function ExpireInfoC(props: { todo: TodoOverviewListItemExpireInfo_Todo$key }) {
  const { expireAt } = useFragment(
    graphql`
      fragment TodoOverviewListItemExpireInfo_Todo on Todo {
        expireAt
      }
    `,
    props.todo
  );
  return (
    <AHText type="detail" font={AHTextFonts.BOLD} style={flexAlignRight}>
      {expireAt ? (
        <>
          <AHIcon size="detail" type="deadline" color="darkText" />{" "}
          {DateTime.fromISO(expireAt).toLocaleString(DateTime.DATETIME_SHORT)}
        </>
      ) : (
        ""
      )}
    </AHText>
  );
}
const ExpireInfo = React.memo(ExpireInfoC);

const useTodoSetStatus = (id: string) => {
  const [updateStatus] =
    useMutation<TodoOverviewListItemSetStatusMutation>(graphql`
      mutation TodoOverviewListItemSetStatusMutation(
        $todoId: ID!
        $newStatus: TodoStatus!
      ) {
        todo(todoId: $todoId) {
          updateTodoItemStatus(input: { status: $newStatus }) {
            todo {
              id
              status

              lastChangedStatusBy {
                id
                name
              }
              lastChangedStatusAt
            }
          }
        }
      }
    `);

  const cb = useCallback(
    (newStatus: TodoStatus) => {
      updateStatus({
        optimisticUpdater: (store) => {
          store.get(id)?.setValue(newStatus, "status");
        },
        variables: {
          todoId: id,
          newStatus,
        },
      });
    },
    [id, updateStatus]
  );
  return cb;
};

const {
  assigneTextStyle,
  flexAlignRight,
  tagContainer,
  flexOne,
  footerStyle,
  titleAndExpire,
  circleWrapper,
  wrapperStylew,
} = StyleSheet.create({
  flexAlignRight: {
    textAlign: "right",
  },
  assigneTextStyle: {
    flex: 1,

    alignItems: "center",
  },
  tagContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  wrapperStylew: {
    borderBottomColor: "#eee",
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: "row",
    justifyContent: "space-between",
    alignContent: "center",
    alignItems: "center",
  },
  flexOne: {
    flex: 1,
  },
  titleAndExpire: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    justifyContent: "space-between",
  },
  footerStyle: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  circleWrapper: {
    justifyContent: "flex-end",
    alignItems: "center",
    flexDirection: "row",
  },
});
function TodoOverviewListItemC(props: TodoListItemProps) {
  const todo = useFragment(
    graphql`
      fragment TodoOverviewListItemWidget_Todo on Todo {
        id
        status
        title
        ...TodoOverviewListItemExpireInfo_Todo
        ...TodoOverviewListItemFooter_Todo
        ...TodoCircleWidgetOverview_Todo
      }
    `,
    props.todo
  );
  const setRemoteStatus = useTodoSetStatus(todo.id);

  const itemId = todo.id;

  const onSetStatus = useCallback(() => {
    if (props.interactive === false) {
      return;
    }
    setRemoteStatus(todo.status === "DONE" ? "PENDING" : "DONE");
  }, [props.interactive, setRemoteStatus, todo.status]);

  const op = props.onPress;

  const onPress = useEvent(() => {
    op?.(itemId, props.isFocused ?? false);
  });

  const sf = useScaleFactor();

  const borderLeftWidth = Math.floor(sf * 4);
  const paddingLeft = useSpaceSize("medium") - borderLeftWidth;
  const theme = useTheme();
  const touchableBoxStyle = useMemo(
    () => [
      wrapperStylew,
      {
        height: PixelRatio.roundToNearestPixel(sf * 40),
        borderLeftWidth,
        borderLeftColor: props.isFocused ? theme.primary : "transparent",
        borderBottomColor: !props.isBottomItem ? "#eee" : "transparent",
        borderBottomWidth: StyleSheet.hairlineWidth,
        paddingLeft,
      },
      props.style,
    ],
    [
      borderLeftWidth,
      paddingLeft,
      props.isBottomItem,
      props.isFocused,
      props.style,
      sf,
      theme.primary,
    ]
  );
  return (
    <TouchableBox
      spaceHorizontal="medium"
      style={touchableBoxStyle}
      color={props.isFocused ? "background" : "surface"}
      spaceVertical="xsmall"
      onPress={onPress}
    >
      <Box spaceVertical="xxsmall" spaceRight="small" style={flexOne}>
        <View style={titleAndExpire}>
          <AHText type="subtitle" numberOfLines={1} style={flexOne}>
            {todo.title}
          </AHText>
          <ExpireInfo todo={todo} />
        </View>
        <Footer todo={todo} />
      </Box>
      <Box spaceLeft="small" style={circleWrapper}>
        <TodoCircle todo={todo} onPress={onSetStatus} />
      </Box>
    </TouchableBox>
  );
}

function Footer(props: { todo: TodoOverviewListItemFooter_Todo$key }) {
  const todo = useFragment(
    graphql`
      fragment TodoOverviewListItemFooter_Todo on Todo {
        tags {
          id
          color
          title
        }
        assigneeText
        assignedUsers {
          user {
            name
          }
          assignedAt
        }
      }
    `,
    props.todo
  );

  const assigneeText =
    todo.assignedUsers
      .filter((u) => u.user?.name)
      .map(({ user }) => {
        if (!user?.name) {
          return "";
        }
        const names = user?.name.split(" ");
        return `${names[0]}${names.length > 1 ? " " + names[1].charAt(0) : ""}`;
      })
      .join(" · ") +
    (todo.assigneeText !== "" ? `·  ${todo.assigneeText}` : "");

  return (
    <View style={footerStyle}>
      {assigneeText.length > 0 ? (
        <AHText
          numberOfLines={1}
          type="detail"
          style={assigneTextStyle}
          spaceRight="small"
        >
          <AHIcon color="darkText" type="users" size="detail" /> {assigneeText}
        </AHText>
      ) : null}
      <View style={tagContainer}>
        {todo.tags.map((tag, i) => (
          <Box key={tag.id} spaceLeft={i === 0 ? "none" : "xsmall"}>
            <AHTag
              color={tag.color}
              type="outside"
              title={tag.title}
              size="small"
            />
          </Box>
        ))}
      </View>
    </View>
  );
}
export const TodoOverviewListItem = React.memo(TodoOverviewListItemC);
